import { useMemo } from 'react'
import { MapContainer, Marker, Popup } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Globe2, Loader2, X } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select'
import { DatePicker } from '@/components/ui/date-picker'
import { OpenStreetMapTiles } from '@/components/maps/OpenStreetMapTiles'
import { useClosedWonMap, useUsers, useProducts } from '@/hooks/use-api'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { useDebounce } from '@/hooks/use-debounce'
import { useUser } from '@/contexts/UserContext'
import { ownerPickerOptions } from '@/lib/salesReps'
import { formatDate } from '@/lib/utils'
import type { ClosedWonMapParams } from '@/types'

// Brand-coloured teardrop pin (inline SVG divIcon) — a white outline + drop
// shadow keep it visible over busy/dark map tiles at high zoom. No asset imports
// (avoids Leaflet's default-marker bundler issue). Anchored at the tip.
const dealIcon = L.divIcon({
  className: '',
  html:
    '<svg width="22" height="30" viewBox="0 0 22 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" ' +
    'style="filter:drop-shadow(0 1px 1.5px rgba(0,0,0,.55))">' +
    '<path d="M11 1C5.5 1 1 5.4 1 10.8c0 6.7 8.4 17.1 9.5 18.4a1 1 0 0 0 1 .4 1 1 0 0 0 .5-.4C13 27.9 21 17.5 21 10.8 21 5.4 16.5 1 11 1z" ' +
    'fill="#941052" stroke="#fff" stroke-width="2"/>' +
    '<circle cx="11" cy="10.8" r="3.2" fill="#fff"/>' +
    '</svg>',
  iconSize: [22, 30],
  iconAnchor: [11, 30],
  popupAnchor: [0, -28],
})

// Accessible, on-brand cluster icon. Colour ramps light -> dark with magnitude
// ("darker = more deals"); text colour flips (dark on the light tier, white on
// the dark tiers) so the count always passes AA contrast. Size also scales.
const createClusterIcon = (cluster: { getChildCount: () => number }) => {
  const count = cluster.getChildCount()
  const { bg, fg, size } =
    count >= 100
      ? { bg: '#072744', fg: '#ffffff', size: 52 } // Prussian, white (~15:1)
      : count >= 25
        ? { bg: '#183E6A', fg: '#ffffff', size: 46 } // Navy, white (~10:1)
        : { bg: '#A0C1DB', fg: '#072744', size: 40 } // Powder Blue, Prussian text (~8:1)
  // Dark text on the light tier needs no shadow; white text gets one for edges.
  const shadow = fg === '#ffffff' ? 'text-shadow:0 1px 2px rgba(0,0,0,.5);' : ''
  return L.divIcon({
    className: '',
    html:
      `<div role="img" aria-label="${count} deals in this area" title="${count} deals in this area" ` +
      `style="width:${size}px;height:${size}px;border-radius:9999px;background:${bg};` +
      `border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.35);` +
      `display:flex;align-items:center;justify-content:center;` +
      `color:${fg};font-weight:700;font-size:13px;${shadow}">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

const cad = (v: number | null) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        maximumFractionDigits: 0,
      }).format(v)

export default function WhereInTheWorld() {
  const { currentUser } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'whereInTheWorld')

  const [ownerIds, setOwnerIds] = usePersistentState<string[]>(fk('owners'), [])
  const [productIds, setProductIds] = usePersistentState<string[]>(fk('products'), [])
  const [minAmount, setMinAmount] = usePersistentState<string>(fk('min'), '')
  const [maxAmount, setMaxAmount] = usePersistentState<string>(fk('max'), '')
  const [closeFrom, setCloseFrom] = usePersistentState<string>(fk('from'), '')
  const [closeTo, setCloseTo] = usePersistentState<string>(fk('to'), '')

  const debMin = useDebounce(minAmount, 400)
  const debMax = useDebounce(maxAmount, 400)

  const { data: usersData } = useUsers({ limit: 10000 })
  const ownerOptions = useMemo(
    () => ownerPickerOptions(usersData?.data ?? []).map((u) => ({ value: u.id, label: u.name })),
    [usersData]
  )
  const { data: productsData } = useProducts({ limit: 10000, sort_by: 'name', sort_order: 'asc' })
  const productOptions = useMemo(
    () => (productsData?.data ?? []).map((p) => ({ value: p.id, label: p.name })),
    [productsData]
  )

  const params = useMemo<ClosedWonMapParams>(
    () => ({
      owner_id: ownerIds.length ? ownerIds.join(',') : undefined,
      product_ids: productIds.length ? productIds.join(',') : undefined,
      amount_min: debMin ? Number(debMin) : undefined,
      amount_max: debMax ? Number(debMax) : undefined,
      close_date_from: closeFrom || undefined,
      close_date_to: closeTo || undefined,
    }),
    [ownerIds, productIds, debMin, debMax, closeFrom, closeTo]
  )

  const hasFilters =
    ownerIds.length > 0 ||
    productIds.length > 0 ||
    minAmount !== '' ||
    maxAmount !== '' ||
    closeFrom !== '' ||
    closeTo !== ''

  const clearFilters = () => {
    setOwnerIds([])
    setProductIds([])
    setMinAmount('')
    setMaxAmount('')
    setCloseFrom('')
    setCloseTo('')
  }

  const { data, isLoading, error } = useClosedWonMap(params)
  const deals = data?.deals ?? []

  const byCountry = useMemo(() => {
    const m = new Map<string, { count: number; total: number }>()
    for (const d of deals) {
      const key = (d.country || 'Unknown').trim() || 'Unknown'
      const entry = m.get(key) || { count: 0, total: 0 }
      entry.count += 1
      entry.total += d.amount_cad || 0
      m.set(key, entry)
    }
    return [...m.entries()].sort((a, b) => b[1].count - a[1].count)
  }, [deals])

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Globe2 className="h-7 w-7 text-primary" aria-hidden="true" />
          Where in the World
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data
            ? `${data.located_count.toLocaleString()} Closed Won deals across ${data.country_count} countries`
            : 'Closed Won deals around the globe'}
          {data && data.unlocated_count > 0
            ? ` · ${data.unlocated_count.toLocaleString()} without a mappable location`
            : ''}
        </p>
      </div>

      {/* Filters: narrow the mapped deals by owner, product, value (CAD) and close date. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Owner</Label>
          <SearchableMultiSelect
            label="Owner"
            options={ownerOptions}
            selected={ownerIds}
            onChange={setOwnerIds}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Product</Label>
          <SearchableMultiSelect
            label="Product"
            options={productOptions}
            selected={productIds}
            onChange={setProductIds}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="min-amount" className="text-xs text-muted-foreground">
            Min value (CAD)
          </Label>
          <Input
            id="min-amount"
            type="number"
            inputMode="numeric"
            min={0}
            className="w-32"
            placeholder="0"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            aria-label="Minimum deal value in Canadian dollars"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="max-amount" className="text-xs text-muted-foreground">
            Max value (CAD)
          </Label>
          <Input
            id="max-amount"
            type="number"
            inputMode="numeric"
            min={0}
            className="w-32"
            placeholder="Any"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
            aria-label="Maximum deal value in Canadian dollars"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Closed from</Label>
          <div className="w-40">
            <DatePicker
              value={closeFrom || null}
              onChange={(v) => setCloseFrom(v ?? '')}
              aria-label="Closed date from"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Closed to</Label>
          <div className="w-40">
            <DatePicker
              value={closeTo || null}
              onChange={(v) => setCloseTo(v ?? '')}
              aria-label="Closed date to"
            />
          </div>
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} aria-label="Clear all filters">
            <X className="mr-1 h-4 w-4" aria-hidden="true" />
            Clear
          </Button>
        )}
      </div>

      {isLoading && (
        <div
          className="flex flex-col items-center justify-center gap-2 py-20 text-center text-muted-foreground"
          role="status"
        >
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
          <div>
            <p>Locating deals…</p>
            <p className="text-sm">
              The first load can take a couple of minutes while we map each location. After that it
              loads quickly.
            </p>
          </div>
        </div>
      )}
      {error && (
        <p className="text-destructive" role="alert">
          Failed to load the map. Please try again.
        </p>
      )}

      {data && (
        <>
          <Card>
            <CardContent className="p-0">
              {/* `isolate` confines Leaflet's z-index:400-1000 panes to their own
                  stacking context so the filter dropdowns / date-picker popovers
                  (portaled at z-50) render above the map instead of behind it. */}
              <div
                className="isolate h-[70vh] w-full overflow-hidden rounded-md"
                role="region"
                aria-label="World map of Closed Won deals"
              >
                <MapContainer
                  center={[20, 0]}
                  zoom={2}
                  minZoom={2}
                  worldCopyJump
                  scrollWheelZoom
                  style={{ height: '100%', width: '100%' }}
                >
                  <OpenStreetMapTiles />
                  <MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterIcon}>
                    {deals.map((d) => (
                      <Marker key={d.id} position={[d.lat, d.lng]} icon={dealIcon}>
                        <Popup>
                          <div className="text-sm">
                            <div className="font-semibold">{d.account_name || d.name}</div>
                            {d.account_name && <div>{d.name}</div>}
                            <div className="text-muted-foreground">
                              {[d.city, d.state, d.country].filter(Boolean).join(', ')}
                            </div>
                            <div className="mt-1">
                              {cad(d.amount_cad)}
                              {' · '}
                              {d.close_date ? formatDate(d.close_date) : '—'}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>
              </div>
            </CardContent>
          </Card>

          {/* Accessible companion: same data as the map, for keyboard/screen-reader users. */}
          <details className="rounded-md border bg-card">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium">
              Deals by country (accessible list)
            </summary>
            <div className="px-4 pb-4 overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Closed Won deal counts and total value (CAD) by country
                </caption>
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th scope="col" className="py-1">
                      Country
                    </th>
                    <th scope="col" className="py-1 text-right">
                      Deals
                    </th>
                    <th scope="col" className="py-1 text-right">
                      Total (CAD)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {byCountry.map(([country, entry]) => (
                    <tr key={country} className="border-t">
                      <td className="py-1">{country}</td>
                      <td className="py-1 text-right">{entry.count.toLocaleString()}</td>
                      <td className="py-1 text-right">{cad(entry.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </div>
  )
}
